/**
 * Servi�o de Autentica��o SEGURO
 * 
 * ?? PRODU��O: Apenas Firebase Auth (UID �nico)
 * ?? DESENVOLVIMENTO: Permite TEST_USERS e fallback local
 */

import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  getIdTokenResult,
  sendPasswordResetEmail,
  User,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import { Role, RegionalId, VendorStatus, UserStatus, CityHallRole } from '../types';
import { normalizeEmail } from '../utils/validation';
import { validateStrongPassword } from '../utils/validators';
import { TEST_USERS } from './testUsers';
import { resolveEffectiveUserRole } from '../utils/resolveVendorByAuthUid';
import { triggerGeocodeVendorAddress } from './geocodeVendorService';
import { toast } from '../components/Toast';
import { onlyL6SearchFields } from '../utils/enrichProductL6Fields';
import type { Product } from '../types';
import { getClientDefaultDeliveryMode } from '../utils/deliveryModePolicyClient';

/** Bloqueia cadastro com senha fraca — usado em register() e fallbacks dev. */
function rejectWeakPassword(password: string): AuthResult | null {
  const passwordCheck = validateStrongPassword(password);
  if (!passwordCheck.valid) {
    return {
      success: false,
      message: `Senha fraca: ${passwordCheck.errors.join('; ')}.`,
    };
  }
  return null;
}

// ============================================
// ?? CONSTANTES DE AMBIENTE
// ============================================
const IS_PRODUCTION = import.meta.env.PROD || import.meta.env.MODE === 'production';
const DEBUG = !IS_PRODUCTION && import.meta.env.DEV;

// Logger seguro - n�o exp�e dados em produ��o
const log = {
  info: (...args: unknown[]) => DEBUG && console.log('[Auth]', ...args),
  warn: (...args: unknown[]) => DEBUG && console.warn('[Auth]', ...args),
  error: (...args: unknown[]) => console.error('[Auth]', ...args), // Erros sempre logados
};

interface AuthResult {
  success: boolean;
  message?: string;
  /** Código Firebase (auth/*, permission-denied, etc.) quando success === false */
  errorCode?: string;
  userId?: string;
  role?: Role;
  regionalId?: RegionalId;
  vendorStatus?: VendorStatus;
  userStatus?: UserStatus;
  name?: string;
  /** Resultado do geocode pós-cadastro VENDOR (não bloqueia success). */
  geocodeOk?: boolean;
  geocodeLocationStatus?: string;
}

interface UserProfile {
  uid: string;
  email: string;
  role: Role;
  name: string;
  regionalId?: RegionalId;
  vendorStatus?: VendorStatus;
  userStatus: UserStatus;
  createdAt: string;
  phone?: string;
  storeName?: string;
  documentType?: 'PF' | 'PJ';
  cpf?: string;
  cnpj?: string;
  cpfCnpj?: string;
  preferences?: string[];
  category?: string;
  vendorType?: 'LIVRE' | 'REGIONAL';
  campaign?: string | null;
  address?: {
    cep?: string;
    logradouro?: string;
    numero?: string;
    bairro?: string;
    cidade?: string;
    uf?: string;
    complemento?: string;
  };
  /** Preenchidos no registro CITY_HALL */
  cityHallRole?: CityHallRole;
  cityHallOrg?: 'AGEFIS' | 'PREFEITURA';
  employeeId?: string;
  department?: string;
}

export type UserRole = Role;

export interface UserWithClaims extends User {
  customClaims?: Record<string, unknown>;
}

// ============================================
// ?? STORAGE LOCAL - APENAS DESENVOLVIMENTO
// ============================================
const LOCAL_USERS_KEY = 'eambulante_local_users_v1';

const getLocalUsers = (): Record<string, UserProfile & { password: string }> => {
  // ?? Em produ��o, n�o usar storage local para auth
  if (IS_PRODUCTION) return {};
  
  try {
    const stored = localStorage.getItem(LOCAL_USERS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

const saveLocalUser = async (email: string, user: UserProfile & { password: string }) => {
            // SUPPLIER: criar documento em suppliers/{uid} se n�o existir
            if (TEST_USERS[email] && TEST_USERS[email].role === 'SUPPLIER') {
              const supplierRef = doc(db, 'suppliers', user.uid);
              const supplierSnap = await getDoc(supplierRef);
                const baseData = supplierSnap.exists() ? supplierSnap.data() : {};
                const needsMigration =
                  !baseData.id ||
                  !baseData.companyName ||
                  baseData.id !== user.uid;

                if (!supplierSnap.exists() || needsMigration) {
                  await setDoc(
                    supplierRef,
                    {
                      ...baseData,
                      id: user.uid,
                      uid: user.uid,
                      companyName:
                        baseData.companyName ||
                        baseData.name ||
                        TEST_USERS[email].name ||
                        email,
                      tradeName:
                        baseData.tradeName ||
                        baseData.name ||
                        TEST_USERS[email].name ||
                        '',
                      email: email,
                      supplierStatus: baseData.supplierStatus ?? 'active',
                      createdAt: baseData.createdAt ?? new Date().toISOString(),
                      updatedAt: new Date().toISOString(),
                    },
                    { merge: true }
                  );
                  log.info(
                    supplierSnap.exists()
                      ? '?? Documento SUPPLIER migrado: suppliers/' + user.uid
                      : '? Documento SUPPLIER criado: suppliers/' + user.uid
                  );
                }
            }
  // ?? Em produ��o, n�o salvar usu�rios locais
  if (IS_PRODUCTION) return;
  
  const users = getLocalUsers();
  // Nunca armazenar senha - apenas perfil sem o campo password
  const { password: _pwd, ...profileWithoutPassword } = user;
  users[email.toLowerCase()] = profileWithoutPassword as UserProfile & { password: string };
  localStorage.setItem(LOCAL_USERS_KEY, JSON.stringify(users));
};

const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      return userDoc.data() as UserProfile;
    }
    return null;
  } catch (error) {
    log.error('Erro ao buscar perfil:', error);
    return null;
  }
};

export const authService = {
  /**
   * Login SEGURO
   * ?? Produ��o: Apenas Firebase Auth
   * ?? Dev: Firebase + TEST_USERS + Local fallback
   */
  async login(emailInput: string, passwordInput: string): Promise<AuthResult> {
    const email = normalizeEmail(emailInput);

    if (!email || !passwordInput) {
      return { success: false, message: 'E-mail e senha s�o obrigat�rios.' };
    }

    log.info('?? Tentando login para:', email);

    // ============================================
    // ?? FIREBASE AUTH - PRIMEIRO para garantir auth.currentUser
    // ?? CR�TICO: Firebase Auth deve ter prioridade para que
    // Firestore rules funcionem (requerem request.auth != null)
    // ============================================
    try {
      log.info('?? Tentando Firebase Auth...');
      const userCredential = await signInWithEmailAndPassword(auth, email, passwordInput);
      const user = userCredential.user;

      const profile = await getUserProfile(user.uid);

      if (!profile) {
        log.warn('Perfil n�o encontrado para UID:', user.uid);
        const tokenResult = await getIdTokenResult(user, false);
        return {
          success: true,
          userId: user.uid,
          role: await resolveEffectiveUserRole(user.uid, (tokenResult.claims.role as Role) || 'CLIENT'),
          name: user.email || 'Usu�rio',
        };
      }

      const effectiveRole = await resolveEffectiveUserRole(user.uid, profile.role);
      log.info(`Login Firebase OK: ${profile.name} (${effectiveRole})`);
      return {
        success: true,
        userId: user.uid,
        role: effectiveRole,
        regionalId: profile.regionalId,
        vendorStatus: profile.vendorStatus,
        userStatus: profile.userStatus || 'active',
        name: profile.name,
      };
    } catch (firebaseError: unknown) {
      const error = firebaseError as { code?: string; message?: string };
      log.warn('?? Firebase falhou:', error.code, error.message);
      
      // ============================================
      // ?? AUTO-CRIA��O: Se TEST_USER n�o existe no Firebase, criar automaticamente
      // ?? CR�TICO: Isso garante auth.currentUser ap�s login
      // ============================================
      if (error.code === 'auth/user-not-found' || error.code === 'auth/invalid-credential') {
        const testUser = TEST_USERS[email];
        if (testUser && testUser.password === passwordInput) {
          log.info('?? TEST_USER detectado mas n�o existe no Firebase. Criando conta...');
          
          try {
            // Criar conta no Firebase Auth
            const userCredential = await createUserWithEmailAndPassword(auth, email, passwordInput);
            const user = userCredential.user;
            
            // Criar perfil no Firestore
            const userProfile = {
              uid: user.uid,
              email: email,
              role: testUser.role,
              name: testUser.name,
              userStatus: testUser.userStatus || 'active',
              createdAt: new Date().toISOString(),
              ...(testUser.regionalId && { regionalId: testUser.regionalId }),
              ...(testUser.vendorStatus && { vendorStatus: testUser.vendorStatus }),
            };
            
            await setDoc(doc(db, 'users', user.uid), userProfile, { merge: true });
            
            // ?? CR�TICO: Se for VENDOR, atualizar o documento vendors/{testUser.userId}
            // com o campo ownerUid apontando para o UID real do Firebase Auth
            // Isso permite que a Cloud Function encontre o UID correto ao criar pedidos
            if (testUser.role === 'VENDOR' && testUser.userId) {
              log.info(`?? Atualizando vendors/${testUser.userId} com ownerUid: ${user.uid}`);
              try {
                await setDoc(doc(db, 'vendors', testUser.userId), {
                  ownerUid: user.uid, // UID real do Firebase Auth
                  ownerName: testUser.name,
                  email: email,
                  updatedAt: serverTimestamp(),
                }, { merge: true });
                log.info('? Vendor atualizado com ownerUid');
              } catch (vendorError) {
                log.warn('?? Erro ao atualizar vendor:', vendorError);
              }
            }
            
            // ??? Se for DRIVER, atualizar o documento drivers/{testUser.userId}
            if (testUser.role === 'DRIVER' && testUser.userId) {
              log.info(`??? Atualizando drivers/${testUser.userId} com ownerUid: ${user.uid}`);
              try {
                await setDoc(doc(db, 'drivers', testUser.userId), {
                  ownerUid: user.uid, // UID real do Firebase Auth
                  email: email,
                  updatedAt: serverTimestamp(),
                }, { merge: true });
                log.info('? Driver atualizado com ownerUid');
              } catch (driverError) {
                log.warn('?? Erro ao atualizar driver:', driverError);
              }
            }
            
            log.info(`? TEST_USER criado no Firebase: ${testUser.name} (UID: ${user.uid})`);
            
            return {
              success: true,
              userId: user.uid, // Usar UID real do Firebase
              role: testUser.role,
              regionalId: testUser.regionalId,
              vendorStatus: testUser.vendorStatus,
              userStatus: testUser.userStatus || 'active',
              name: testUser.name,
            };
          } catch (createError: any) {
            // Se conta j� existe (race condition), tentar login novamente
            if (createError.code === 'auth/email-already-in-use') {
              log.info('?? Conta j� existe, tentando login novamente...');
              try {
                const retryCredential = await signInWithEmailAndPassword(auth, email, passwordInput);
                const profile = await getUserProfile(retryCredential.user.uid);
                return {
                  success: true,
                  userId: retryCredential.user.uid,
                  role: profile?.role || testUser.role,
                  regionalId: profile?.regionalId || testUser.regionalId,
                  vendorStatus: profile?.vendorStatus || testUser.vendorStatus,
                  userStatus: profile?.userStatus || 'active',
                  name: profile?.name || testUser.name,
                };
              } catch (retryError) {
                log.error('? Retry tamb�m falhou:', retryError);
              }
            }
            log.error('? Falha ao criar TEST_USER no Firebase:', createError);
          }
        }
        
        return { success: false, message: 'E-mail ou senha incorretos.' };
      }
      
      // ============================================
      // ?? FALLBACK OFFLINE: TEST_USERS quando sem conex�o
      // ============================================
      if (error.code === 'auth/network-request-failed') {
        // Tentar TEST_USERS como fallback offline
        const testUser = TEST_USERS[email];
        if (testUser && testUser.password === passwordInput) {
          log.info('? Login via TEST_USERS (fallback offline):', testUser.name);
          // ?? AVISO: orders n�o funcionar�o pois auth.currentUser = null
          return {
            success: true,
            userId: testUser.userId,
            role: testUser.role,
            regionalId: testUser.regionalId,
            vendorStatus: testUser.vendorStatus,
            userStatus: testUser.userStatus || 'active',
            name: testUser.name,
          };
        }

        // Local users fallback (dev only)
        if (!IS_PRODUCTION) {
          const localUsers = getLocalUsers();
          const localUser = localUsers[email];
          if (localUser && localUser.password === passwordInput) {
            log.info('? Login via LOCAL (fallback offline):', localUser.name);
            return {
              success: true,
              userId: localUser.uid,
              role: localUser.role,
              regionalId: localUser.regionalId,
              vendorStatus: localUser.vendorStatus,
              userStatus: localUser.userStatus || 'active',
              name: localUser.name,
            };
          }
        }

        const message = IS_PRODUCTION 
          ? 'Sem conex�o. Verifique sua internet.'
          : 'Sem conex�o. Verifique sua internet ou use uma conta de teste.';
        return { success: false, message };
      }
      
      if (error.code === 'auth/wrong-password') {
        return { success: false, message: 'E-mail ou senha incorretos.' };
      }
      if (error.code === 'auth/invalid-email') {
        return { success: false, message: 'E-mail inv�lido.' };
      }
      if (error.code === 'auth/too-many-requests') {
        return { success: false, message: 'Muitas tentativas. Tente novamente em alguns minutos.' };
      }
      if (error.code === 'auth/unauthorized-domain') {
        return {
          success: false,
          message:
            'Este domínio não está autorizado no Firebase Auth. Adicione "localhost" em Authentication → Settings → Authorized domains.',
        };
      }

      return { success: false, message: 'Erro ao fazer login. Tente novamente.' };
    }
  },

  /**
   * Registro SEGURO
   * ?? Produ��o: Apenas Firebase Auth
   * ?? Dev: Firebase + fallback local (offline)
   */
  async register(
    email: string,
    password: string,
    profile: Omit<UserProfile, 'uid' | 'email' | 'createdAt'>
  ): Promise<AuthResult> {
    const normalizedEmail = normalizeEmail(email);

    if (!normalizedEmail || !password) {
      return { success: false, message: 'E-mail e senha s�o obrigat�rios.' };
    }
    const weakPassword = rejectWeakPassword(password);
    if (weakPassword) {
      return weakPassword;
    }

    log.info('?? Registrando:', normalizedEmail);

    // ?? Verificar se � email de teste (bloqueado em prod)
    if (TEST_USERS[normalizedEmail]) {
      return { success: false, message: 'Este e-mail � reservado. Use outro e-mail.' };
    }

    // ?? Verificar local users (apenas dev)
    if (!IS_PRODUCTION) {
      const localUsers = getLocalUsers();
      if (localUsers[normalizedEmail]) {
        return { success: false, message: 'Este e-mail j� est� cadastrado. Tente fazer login.' };
      }
    }

    // ============================================
    // ?? FIREBASE AUTH - Registro principal
    // ============================================
    try {
      log.info('?? Tentando registro Firebase...');

      // Pr�-registro para Cloud Function
      if (profile.role) {
        const tempId = normalizedEmail.replace(/[^a-zA-Z0-9]/g, '_');
        await setDoc(
          doc(db, 'pendingRegistrations', tempId),
          {
            email: normalizedEmail,
            role: profile.role,
            createdAt: serverTimestamp(),
          },
          { merge: true }
        );
      }

      const userCredential = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
      const user = userCredential.user;

      // Vendedores, fornecedores e motoboys aguardam aprova��o do Owner
      // Clientes entram direto - s�o a base do neg�cio
      const requiresApproval = profile.role === 'VENDOR' || profile.role === 'SUPPLIER'
        || profile.role === 'DRIVER' || profile.role === 'CITY_HALL';
      const resolvedStatus: UserStatus = requiresApproval ? 'pending_approval' : (profile.userStatus || 'active');

      // Criar perfil (sem campos undefined - Firestore n�o aceita)
      const userProfile: UserProfile = {
        uid: user.uid,
        email: normalizedEmail,
        role: profile.role,
        name: profile.name,
        userStatus: resolvedStatus,
        createdAt: new Date().toISOString(),
      };

      // Campos opcionais
      if (profile.regionalId !== undefined) userProfile.regionalId = profile.regionalId;
      if (profile.vendorStatus !== undefined) userProfile.vendorStatus = profile.vendorStatus;
      if (profile.phone !== undefined) userProfile.phone = profile.phone;
      if (profile.storeName !== undefined) userProfile.storeName = profile.storeName;
      if (profile.documentType !== undefined) userProfile.documentType = profile.documentType;
      if (profile.cpf !== undefined) userProfile.cpf = profile.cpf;
      if (profile.cnpj !== undefined) userProfile.cnpj = profile.cnpj;
      if (profile.cpfCnpj !== undefined) userProfile.cpfCnpj = profile.cpfCnpj;
      if (profile.preferences !== undefined && profile.preferences.length > 0) userProfile.preferences = profile.preferences;
      if (profile.category !== undefined) userProfile.category = profile.category;
      if (profile.vendorType !== undefined) (userProfile as UserProfile).vendorType = profile.vendorType;
      if (profile.campaign !== undefined) (userProfile as UserProfile).campaign = profile.campaign;
      if (profile.role === 'CITY_HALL') {
        if (profile.cityHallRole !== undefined) userProfile.cityHallRole = profile.cityHallRole;
        if (profile.cityHallOrg !== undefined) userProfile.cityHallOrg = profile.cityHallOrg;
        if (profile.employeeId !== undefined) userProfile.employeeId = profile.employeeId;
        if (profile.department !== undefined) userProfile.department = profile.department;
      }

      await setDoc(doc(db, 'users', user.uid), userProfile, { merge: true });

      let geocodeOk: boolean | undefined;
      let geocodeLocationStatus: string | undefined;

      // ?? VENDOR: criar documento em vendors/{uid} automaticamente no registro
      // Necess�rio para aparecer na Fila de Aprova��o do Owner
      if (profile.role === 'VENDOR') {
        try {
          await setDoc(doc(db, 'vendors', user.uid), {
            id: user.uid,
            name: profile.name || normalizedEmail,
            ownerName: profile.name || '',
            storeName: profile.storeName || profile.name || '',
            email: normalizedEmail,
            phone: profile.phone || '',
            image: 'https://images.unsplash.com/photo-1565958011703-44f9829ba187?w=400',
            location: null,
            locationStatus: 'PENDING_ADDRESS',
            ...(profile.address ? { address: profile.address } : {}),
            vendorStatus: profile.vendorStatus || 'PENDING_VERIFICATION',
            subscriptionStatus: 'inactive',
            rating: 0,
            ratingCount: 0,
            documentType: profile.documentType || undefined,
            cpf: profile.documentType === 'PF' ? (profile.cpf || undefined) : undefined,
            cnpj: profile.documentType === 'PJ' ? (profile.cnpj || undefined) : undefined,
            cpfCnpj: profile.cpfCnpj || profile.cpf || profile.cnpj || undefined,
            category: profile.category || undefined,
            regionalId: profile.regionalId || undefined,
            ...(profile.vendorType ? { vendorType: profile.vendorType } : {}),
            ...(profile.campaign ? { campaign: profile.campaign } : {}),
            deliveryMode: getClientDefaultDeliveryMode(),
            storeOnline: false,
            createdAt: new Date().toISOString(),
          }, { merge: true });
          log.info('? Documento VENDOR criado: vendors/' + user.uid);
          // P0 — geocode com await/retry; falha NÃO bloqueia a conta.
          try {
            const geo = await triggerGeocodeVendorAddress(
              user.uid,
              profile.address || null
            );
            geocodeOk = geo.ok;
            geocodeLocationStatus = geo.locationStatus;
            if (geo.ok && geo.locationStatus === 'CONFIRMED') {
              toast.success('Endereço confirmado', 'Sua loja poderá aparecer na vitrine após aprovação.');
            } else if (geo.ok) {
              toast.warning(
                'Endereço em análise',
                'Confirmaremos o endereço automaticamente. Se precisar, o Owner pode revalidar na aprovação.'
              );
            } else {
              toast.warning(
                'Endereço pendente',
                'Conta criada. O endereço será confirmado na aprovação ou em nova tentativa.'
              );
            }
          } catch (geoErr) {
            geocodeOk = false;
            log.warn('geocode pós-registro falhou (não bloqueante):', geoErr);
            toast.warning(
              'Endereço pendente',
              'Conta criada. O endereço será confirmado na aprovação ou em nova tentativa.'
            );
          }
        } catch (vendorError) {
          log.warn('?? Erro ao criar documento VENDOR:', vendorError);
        }
      }

      // ?? SUPPLIER: criar documento em suppliers/{uid} automaticamente no registro
      if (profile.role === 'SUPPLIER') {
        try {
          await setDoc(doc(db, 'suppliers', user.uid), {
            id: user.uid,
            uid: user.uid,
            companyName: profile.storeName || profile.name || normalizedEmail,
            tradeName: profile.storeName || profile.name || '',
            email: normalizedEmail,
            phone: profile.phone || '',
            supplierStatus: 'pending',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          }, { merge: true });
          log.info('? Documento SUPPLIER criado: suppliers/' + user.uid);
        } catch (supplierError) {
          log.warn('?? Erro ao criar documento SUPPLIER:', supplierError);
        }
      }

      // ?? DRIVER: criar documento em drivers/{uid} automaticamente no registro
      if (profile.role === 'DRIVER') {
        try {
          const driverData: Record<string, unknown> = {
            id: user.uid,
            uid: user.uid,
            authUserId: user.uid,
            name: profile.name || normalizedEmail,
            email: normalizedEmail,
            phone: profile.phone || '',
            status: 'pending_approval',
            available: false,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          // @ts-expect-error profile extension fields from registration payload
          if (profile.vehicleType) driverData.vehicleType = profile.vehicleType;
          // @ts-expect-error profile extension fields from registration payload
          if (profile.cnh) driverData.cnh = profile.cnh;
          // @ts-expect-error profile extension fields from registration payload
          if (profile.licensePlate) driverData.licensePlate = profile.licensePlate;
          // @ts-expect-error profile extension fields from registration payload
          if (profile.serviceArea) driverData.serviceArea = profile.serviceArea;
          // @ts-expect-error profile extension fields from registration payload
          if (profile.isFlexDriver) driverData.isFlexDriver = profile.isFlexDriver;

          await setDoc(doc(db, 'drivers', user.uid), driverData, { merge: true });
          log.info('?? Documento DRIVER criado: drivers/' + user.uid);
        } catch (driverError) {
          log.warn('?? Erro ao criar documento DRIVER:', driverError);
        }
      }

      // ?? Backup local apenas em dev (sem senha em prod!)
      if (!IS_PRODUCTION) {
        saveLocalUser(normalizedEmail, { ...userProfile, password });
      }

      log.info('? Registro Firebase OK:', user.uid);

      return {
        success: true,
        userId: user.uid,
        role: profile.role,
        regionalId: profile.regionalId,
        vendorStatus: profile.vendorStatus,
        userStatus: profile.userStatus,
        name: profile.name,
        geocodeOk,
        geocodeLocationStatus,
      };
    } catch (firebaseError: unknown) {
      const error = firebaseError as { code?: string; message?: string; name?: string; stack?: string };
      // Sempre logar o erro bruto (produção) para depuração — não mostrar isso no toast
      log.error('Registro falhou (código, mensagem, stack):', error.code, error.message, error.stack, firebaseError);

      // ============================================
      // ?? FALLBACK LOCAL - APENAS DESENVOLVIMENTO
      // ============================================
      if (!IS_PRODUCTION && (
          error.code === 'auth/network-request-failed' || 
          error.code === 'auth/internal-error' ||
          error.code === 'auth/operation-not-allowed' ||
          error.message?.includes('network')
      )) {
        const weakOnFallback = rejectWeakPassword(password);
        if (weakOnFallback) {
          return weakOnFallback;
        }

        log.info('?? [DEV] Modo offline - criando usu�rio local');
        
        const localUserId = `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const userProfile: UserProfile & { password: string } = {
          uid: localUserId,
          email: normalizedEmail,
          ...profile,
          password,
          createdAt: new Date().toISOString(),
        };

        saveLocalUser(normalizedEmail, userProfile);
        log.info('? [DEV] Usu�rio local criado:', localUserId);

        return {
          success: true,
          userId: localUserId,
          role: profile.role,
          regionalId: profile.regionalId,
          vendorStatus: profile.vendorStatus,
          userStatus: profile.userStatus,
          name: profile.name,
        };
      }

      // Erros espec�ficos do Firebase
      if (error.code === 'auth/email-already-in-use') {
        return { success: false, errorCode: error.code, message: 'Este e-mail já está cadastrado. Tente fazer login.' };
      }
      if (error.code === 'auth/invalid-email') {
        return { success: false, errorCode: error.code, message: 'E-mail inválido. Verifique o formato.' };
      }
      if (error.code === 'auth/weak-password') {
        return {
          success: false,
          errorCode: error.code,
          message: 'Senha muito fraca. Use no mínimo 8 caracteres, com maiúscula, minúscula, número e caractere especial.',
        };
      }
      if (error.code === 'permission-denied') {
        return {
          success: false,
          errorCode: error.code,
          message: 'Acesso negado ao salvar o cadastro (permissão Firestore). Tente de novo em instantes ou contate o suporte.',
        };
      }
      if (error.code === 'auth/too-many-requests') {
        return { success: false, errorCode: error.code, message: 'Muitas tentativas. Aguarde alguns minutos e tente novamente.' };
      }
      if (error.code === 'auth/network-request-failed') {
        return { success: false, errorCode: error.code, message: 'Sem conexão com a internet. Verifique e tente de novo.' };
      }
      if (error.code === 'auth/operation-not-allowed') {
        // ?? Em produ��o: erro real
        if (IS_PRODUCTION) {
          return { success: false, errorCode: error.code, message: 'Cadastro temporariamente indisponível. Tente novamente.' };
        }
        const weakOnFallback = rejectWeakPassword(password);
        if (weakOnFallback) {
          return weakOnFallback;
        }

        log.info('?? [DEV] Firebase Auth n�o habilitado - criando local');
        
        const localUserId = `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const userProfile: UserProfile & { password: string } = {
          uid: localUserId,
          email: normalizedEmail,
          ...profile,
          password,
          createdAt: new Date().toISOString(),
        };

        saveLocalUser(normalizedEmail, userProfile);

        return {
          success: true,
          userId: localUserId,
          role: profile.role,
          regionalId: profile.regionalId,
          vendorStatus: profile.vendorStatus,
          userStatus: profile.userStatus,
          name: profile.name,
        };
      }

      return {
        success: false,
        errorCode: error.code,
        message: error.message
          ? `Erro ao criar conta: ${error.code || 'desconhecido'} — ${error.message}`
          : 'Erro ao criar conta. Tente novamente.',
      };
    }
  },

  async logout(): Promise<void> {
    try {
      await signOut(auth);
      log.info('? Logout realizado');
    } catch {
      log.info('Logout local');
    }
  },

  getCurrentUser(): User | null {
    return auth.currentUser;
  },

  async getCurrentUserRole(forceRefresh = false): Promise<UserRole | undefined> {
    const user = auth.currentUser;
    if (!user) return undefined;
    try {
      const tokenResult = await getIdTokenResult(user, forceRefresh);
      return (tokenResult.claims.role as UserRole) || undefined;
    } catch {
      return undefined;
    }
  },

  hasRole(role: UserRole): Promise<boolean> {
    return this.getCurrentUserRole().then((current) => current === role);
  },

  hasAnyRole(roles: UserRole[]): Promise<boolean> {
    return this.getCurrentUserRole().then((current) => (current ? roles.includes(current) : false));
  },

  async forceRefreshToken(): Promise<void> {
    const user = auth.currentUser;
    if (user) {
      await user.getIdToken(true);
    }
  },

  onAuthChange(callback: (user: UserWithClaims | null) => void) {
    return onAuthStateChanged(auth, async (user) => {
      if (!user) {
        callback(null);
        return;
      }
      try {
        const tokenResult = await getIdTokenResult(user, false);
        const userWithClaims: UserWithClaims = Object.assign(user, {
          customClaims: tokenResult.claims,
        });
        callback(userWithClaims);
      } catch {
        callback(null);
      }
    });
  },

  /**
   * Recupera��o de Senha via Firebase
   * Envia email com link para redefinir a senha
   */
  async sendPasswordReset(emailInput: string): Promise<AuthResult> {
    const email = normalizeEmail(emailInput);

    if (!email) {
      return { success: false, message: 'Por favor, informe seu e-mail.' };
    }

    log.info('?? Enviando email de recupera��o para:', email);

    // ?? Verificar se � usu�rio de teste (bloqueado em prod, mensagem diferente em dev)
    const testUser = TEST_USERS[email];
    if (testUser) {
      return {
        success: true,
        message: 'Se este email estiver cadastrado, você receberá instruções.',
      };
    }

    // ?? Verificar usu�rio local (apenas dev)
    if (!IS_PRODUCTION) {
      const localUsers = getLocalUsers();
      if (localUsers[email]) {
        return { 
          success: false, 
          message: 'Conta local detectada. Entre em contato com o suporte.' 
        };
      }
    }

    try {
      await sendPasswordResetEmail(auth, email, {
        url: window.location.origin,
        handleCodeInApp: false,
      });

      log.info('? Email de recupera��o enviado!');

      return {
        success: true,
        message: 'Email enviado! Verifique sua caixa de entrada e spam.',
      };
    } catch (firebaseError: unknown) {
      const error = firebaseError as { code?: string };
      log.error('? Erro ao enviar email:', error.code);

      switch (error.code) {
        case 'auth/user-not-found':
          // Por seguran�a, n�o revelamos se o email existe ou n�o
          return {
            success: true,
            message: 'Se este email estiver cadastrado, voc� receber� instru��es de recupera��o.',
          };
        case 'auth/invalid-email':
          return { success: false, message: 'E-mail inv�lido. Verifique o formato.' };
        case 'auth/too-many-requests':
          return { success: false, message: 'Muitas tentativas. Aguarde alguns minutos.' };
        case 'auth/network-request-failed':
          return { success: false, message: 'Sem conex�o com a internet. Tente novamente.' };
        default:
          return { success: false, message: 'Erro ao enviar email. Tente novamente.' };
      }
    }
  },

  /**
   * Completa perfil VENDOR após Phone Auth (usuário já autenticado).
   * Idempotente — merge em users + vendors. Não cria Auth.
   */
  async ensureVendorProfileAfterAuth(profile: {
    name: string;
    phone: string;
    storeName?: string;
    documentType: 'PF' | 'PJ';
    cpf?: string;
    cnpj?: string;
    cpfCnpj?: string;
    category?: string;
    vendorType: 'LIVRE' | 'REGIONAL';
    regionalId?: RegionalId;
    campaign?: string | null;
    address?: UserProfile['address'];
    firstProduct?: { name: string; price: number; image?: string };
  }): Promise<AuthResult> {
    const user = auth.currentUser;
    if (!user) {
      return { success: false, message: 'Sessão expirada. Confirme o SMS novamente.' };
    }

    const phone = String(profile.phone || '').replace(/\D/g, '');
    const email =
      user.email ||
      (phone ? `wa_${phone}@phone.eambulante.local` : `uid_${user.uid}@phone.eambulante.local`);

    try {
      const pendingId = phone
        ? phone.replace(/[^a-zA-Z0-9]/g, '_')
        : user.uid.replace(/[^a-zA-Z0-9]/g, '_');
      await setDoc(
        doc(db, 'pendingRegistrations', pendingId),
        { email, phone: profile.phone, role: 'VENDOR', createdAt: serverTimestamp() },
        { merge: true }
      );

      await setDoc(
        doc(db, 'users', user.uid),
        {
          uid: user.uid,
          email,
          role: 'VENDOR',
          name: profile.name,
          phone: profile.phone,
          storeName: profile.storeName || profile.name,
          userStatus: 'pending_approval',
          vendorStatus: 'PENDING_VERIFICATION',
          documentType: profile.documentType,
          cpf: profile.documentType === 'PF' ? profile.cpf : undefined,
          cnpj: profile.documentType === 'PJ' ? profile.cnpj : undefined,
          cpfCnpj: profile.cpfCnpj || profile.cpf || profile.cnpj,
          category: profile.category,
          vendorType: profile.vendorType,
          campaign: profile.campaign || null,
          regionalId: profile.regionalId,
          createdAt: new Date().toISOString(),
        },
        { merge: true }
      );

      await setDoc(
        doc(db, 'vendors', user.uid),
        {
          id: user.uid,
          name: profile.name,
          ownerName: profile.name,
          storeName: profile.storeName || profile.name,
          email,
          phone: profile.phone,
          image: 'https://images.unsplash.com/photo-1565958011703-44f9829ba187?w=400',
          location: null,
          locationStatus: 'PENDING_ADDRESS',
          ...(profile.address ? { address: profile.address } : {}),
          vendorStatus: 'PENDING_VERIFICATION',
          subscriptionStatus: 'inactive',
          rating: 0,
          ratingCount: 0,
          documentType: profile.documentType,
          cpf: profile.documentType === 'PF' ? profile.cpf : undefined,
          cnpj: profile.documentType === 'PJ' ? profile.cnpj : undefined,
          cpfCnpj: profile.cpfCnpj || profile.cpf || profile.cnpj,
          category: profile.category,
          vendorType: profile.vendorType,
          campaign: profile.campaign || null,
          regionalId: profile.regionalId,
          deliveryMode: getClientDefaultDeliveryMode(),
          createdAt: new Date().toISOString(),
        },
        { merge: true }
      );

      let geocodeOk = false;
      let geocodeLocationStatus: string | undefined;
      try {
        const geo = await triggerGeocodeVendorAddress(user.uid, profile.address || null);
        geocodeOk = geo.ok;
        geocodeLocationStatus = geo.locationStatus;
        if (geo.ok && geo.locationStatus === 'CONFIRMED') {
          toast.success('Endereço confirmado', 'Sua loja poderá aparecer na vitrine após aprovação.');
        } else if (geo.ok) {
          toast.warning(
            'Endereço em análise',
            'Confirmaremos o endereço automaticamente. Se precisar, o Owner pode revalidar na aprovação.'
          );
        } else {
          toast.warning(
            'Endereço pendente',
            'Conta criada. O endereço será confirmado na aprovação ou em nova tentativa.'
          );
        }
      } catch (geoErr) {
        log.warn('geocode pós-registro (phone) falhou (não bloqueante):', geoErr);
        toast.warning(
          'Endereço pendente',
          'Conta criada. O endereço será confirmado na aprovação ou em nova tentativa.'
        );
      }

      if (profile.firstProduct?.name && Number(profile.firstProduct.price) > 0) {
        const productId = `p_${user.uid.slice(0, 8)}_${Date.now().toString(36)}`;
        // Payload legado intacto — L6 só ADICIONA os 4 campos de escala.
        const base = {
          id: productId,
          vendorId: user.uid,
          name: profile.firstProduct.name.trim(),
          price: Number(profile.firstProduct.price),
          image: profile.firstProduct.image || '',
          category: profile.category || 'geral',
          available: true,
          createdAt: new Date().toISOString(),
        };
        const l6 = onlyL6SearchFields({
          ...base,
          description: { pt: '', en: '', es: '' },
          isPaused: false,
        } as Product);
        await setDoc(doc(db, 'products', productId), { ...base, ...l6 });
      }

      try {
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const { getApp } = await import('firebase/app');
        const fn = httpsCallable(getFunctions(getApp(), 'us-central1'), 'setVendorClaimsPhone');
        await fn({ role: 'VENDOR' });
      } catch {
        // Claims podem vir do onCreate; não bloqueia
      }

      return {
        success: true,
        userId: user.uid,
        role: 'VENDOR',
        vendorStatus: 'PENDING_VERIFICATION',
        userStatus: 'pending_approval',
        name: profile.name,
        geocodeOk,
        geocodeLocationStatus,
      };
    } catch (e) {
      log.error('ensureVendorProfileAfterAuth falhou', e);
      return { success: false, message: 'Não foi possível salvar sua loja. Tente de novo.' };
    }
  },
};
